import {
  Serviceability,
  Hub,
  Connection,
  RoutingNode,
  RoutingEdge,
  Address,
  RouteLeg,
  Route,
  RoutingRequest,
} from '@/types/data';

// Node ID builders
export function buildLocalityId(
  network: string,
  postcode: string,
  suburb: string,
  state: string
): string {
  return `${network}-${postcode}-${suburb}-${state}`.toUpperCase();
}

export function buildHubId(network: string, hubName: string): string {
  return `${network}-HUB-${hubName}`.toUpperCase();
}

export function buildCourierZoneId(network: string, courier: string, zone: string): string {
  return `${network}-ZONE-${courier}-${zone}`.toUpperCase();
}

// Build nodes from CSV data (V2 format using connections)
export function buildNodes(
  network: string,
  serviceability: Serviceability[],
  hubs: Hub[],
  connections: Connection[]
): RoutingNode[] {
  const nodes: RoutingNode[] = [];

  // 1. Hub nodes
  for (const hub of hubs) {
    nodes.push({
      id: buildHubId(network, hub.hub),
      name: hub.hub,
      network,
      type: 'Hub',
      data: {
        streetAddress: hub.street,
        suburb: hub.suburb,
        state: hub.state,
        postcode: hub.postcode,
        countryCode: hub.country_code,
      },
    });
  }

  // 2. Locality nodes (unique by postcode+suburb+state)
  const seenLocalities = new Set<string>();
  for (const row of serviceability) {
    if (row.dropoff === 'y' || row.pickup === 'y') {
      const id = buildLocalityId(network, row.postcode, row.suburb, row.state);
      if (!seenLocalities.has(id)) {
        seenLocalities.add(id);
        nodes.push({
          id,
          name: row.suburb,
          network,
          type: 'Locality',
          data: { suburb: row.suburb, postcode: row.postcode, state: row.state },
        });
      }
    }
  }

  // 3. Courier zone nodes (unique) - from serviceability and connections
  const seenZones = new Set<string>();

  // From serviceability
  for (const row of serviceability) {
    if (row.pickup === 'y') {
      const id = buildCourierZoneId(network, row.courier, row.pickup_zone);
      if (!seenZones.has(id)) {
        seenZones.add(id);
        nodes.push({
          id,
          name: row.pickup_zone,
          network,
          type: 'CourierZone',
          data: { courier: row.courier },
        });
      }
    }
    if (row.dropoff === 'y') {
      const id = buildCourierZoneId(network, row.courier, row.dropoff_zone);
      if (!seenZones.has(id)) {
        seenZones.add(id);
        nodes.push({
          id,
          name: row.dropoff_zone,
          network,
          type: 'CourierZone',
          data: { courier: row.courier },
        });
      }
    }
  }

  // From connections (for zones that might only appear in connections)
  for (const conn of connections) {
    const id = buildCourierZoneId(network, conn.courier, conn.zone);
    if (!seenZones.has(id)) {
      seenZones.add(id);
      nodes.push({
        id,
        name: conn.zone,
        network,
        type: 'CourierZone',
        data: { courier: conn.courier },
      });
    }
  }

  return nodes;
}

// Build edges from V2 connections format
// V2 connections.csv format interpretation:
// - start=zone, end=hub → PICKUP (zone's localities can be picked up and sent to this hub)
// - start=hub, end=zone → DROPOFF (hub can deliver to zone's localities)
// - start=hub, end=hub → TRANSFER (hub-to-hub connection)
export function buildEdgesV2(
  network: string,
  serviceability: Serviceability[],
  connections: Connection[],
  hubs: Hub[]
): RoutingEdge[] {
  const edges: RoutingEdge[] = [];
  const hubNames = new Set(hubs.map((h) => h.hub.toUpperCase()));

  // Helper: Check if a name is a hub
  const isHub = (name: string) => hubNames.has(name.toUpperCase());

  // 1. Process connections to determine edge types
  // Group connections by type
  const pickupConnections: Connection[] = []; // zone → hub
  const dropoffConnections: Connection[] = []; // hub → zone
  const transferConnections: Connection[] = []; // hub → hub

  for (const conn of connections) {
    const startIsHub = isHub(conn.start);
    const endIsHub = isHub(conn.end);

    if (startIsHub && endIsHub) {
      // Hub → Hub = TRANSFER
      transferConnections.push(conn);
    } else if (!startIsHub && endIsHub) {
      // Zone → Hub = PICKUP connection info
      pickupConnections.push(conn);
    } else if (startIsHub && !endIsHub) {
      // Hub → Zone = DROPOFF connection info
      dropoffConnections.push(conn);
    }
  }

  // 2. Create TRANSFER edges (hub → hub)
  for (const conn of transferConnections) {
    edges.push({
      start_id: buildHubId(network, conn.start),
      end_id: buildHubId(network, conn.end),
      label: 'TRANSFER',
      courier_zone_id: buildCourierZoneId(network, conn.courier, conn.zone),
      network,
      account: conn.account || null,
      priority: parseInt(conn.priority) || 0,
      booking_trigger_book_on_scan: conn.book_on_scan === 'y',
      booking_trigger_book_on_booking: conn.book_on_booking === 'y',
      booking_trigger_book_on_tracking_from_previous_leg:
        conn.book_on_tracking_from_previous_leg === 'y',
    });
  }

  // Helper: Find pickup connections for a courier+zone (zone → hub)
  const getPickupConnectionsForZone = (courier: string, zone: string): Connection[] => {
    return pickupConnections.filter(
      (c) => c.courier === courier && c.start.toUpperCase() === zone.toUpperCase()
    );
  };

  // Helper: Find dropoff connections for a courier+zone (hub → zone)
  const getDropoffConnectionsForZone = (courier: string, zone: string): Connection[] => {
    return dropoffConnections.filter(
      (c) => c.courier === courier && c.end.toUpperCase() === zone.toUpperCase()
    );
  };

  // 3. Create PICKUP and DROPOFF edges from serviceability
  for (const row of serviceability) {
    const localityId = buildLocalityId(network, row.postcode, row.suburb, row.state);

    // PICKUP edges (locality → hub)
    if (row.pickup.toLowerCase() === 'y') {
      const pickupConns = getPickupConnectionsForZone(row.courier, row.pickup_zone);

      for (const conn of pickupConns) {
        edges.push({
          start_id: localityId,
          end_id: buildHubId(network, conn.end),
          label: 'PICKUP',
          courier_zone_id: buildCourierZoneId(network, row.courier, row.pickup_zone),
          network,
          account: conn.account || null,
          priority: parseInt(conn.priority) || 0,
          booking_trigger_book_on_scan: conn.book_on_scan === 'y',
          booking_trigger_book_on_booking: conn.book_on_booking === 'y',
          booking_trigger_book_on_tracking_from_previous_leg:
            conn.book_on_tracking_from_previous_leg === 'y',
        });
      }
    }

    // DROPOFF edges (hub → locality)
    if (row.dropoff.toLowerCase() === 'y') {
      const dropoffConns = getDropoffConnectionsForZone(row.courier, row.dropoff_zone);

      for (const conn of dropoffConns) {
        edges.push({
          start_id: buildHubId(network, conn.start),
          end_id: localityId,
          label: 'DROPOFF',
          courier_zone_id: buildCourierZoneId(network, row.courier, row.dropoff_zone),
          network,
          account: conn.account || null,
          priority: parseInt(conn.priority) || 0,
          booking_trigger_book_on_scan: conn.book_on_scan === 'y',
          booking_trigger_book_on_booking: conn.book_on_booking === 'y',
          booking_trigger_book_on_tracking_from_previous_leg:
            conn.book_on_tracking_from_previous_leg === 'y',
        });
      }
    }
  }

  return edges;
}

// Path result interface for route finding
interface PathResult {
  path: string[];
  edges: RoutingEdge[];
  depth: number;
  totalPriority: number;
}

// Find all possible routes and return the best one
// Best = shortest depth first, then lowest total priority
export function findBestRoute(
  fromId: string,
  toId: string,
  edges: RoutingEdge[],
  network: string,
  maxDepth: number = 10
): PathResult | null {
  // Early validation
  if (fromId === toId) {
    return { path: [fromId], edges: [], depth: 0, totalPriority: 0 };
  }

  // Build adjacency list for efficient lookups
  const adjacency = new Map<string, RoutingEdge[]>();

  for (const edge of edges) {
    if (edge.network === network || edge.network === 'all') {
      const key = edge.start_id;
      if (!adjacency.has(key)) {
        adjacency.set(key, []);
      }
      adjacency.get(key)!.push(edge);
    }
  }

  // Check if start node has any outgoing edges
  if (!adjacency.has(fromId)) {
    return null;
  }

  // BFS collecting all possible routes that reach destination
  const queue: PathResult[] = [
    {
      path: [fromId],
      edges: [],
      depth: 0,
      totalPriority: 0,
    },
  ];

  const results: PathResult[] = [];
  const maxIterations = 50000; // Safety limit
  let iterations = 0;

  // Track minimum depth found so far for pruning
  let minDepthFound = Infinity;

  while (queue.length > 0 && iterations < maxIterations) {
    iterations++;
    const current = queue.shift()!;
    const lastNode = current.path[current.path.length - 1];

    // Skip if exceeded max depth or if we already have shorter routes
    if (current.depth > maxDepth) continue;
    if (current.depth > minDepthFound) continue; // Prune paths longer than best found

    // Get all edges from current node
    const outEdges = adjacency.get(lastNode) || [];

    for (const edge of outEdges) {
      // Avoid cycles - don't revisit nodes in current path
      if (current.path.includes(edge.end_id)) continue;

      const newPath = [...current.path, edge.end_id];
      const newEdges = [...current.edges, edge];
      const newPriority = current.totalPriority + edge.priority;
      const newDepth = current.depth + 1;

      // Check if we reached destination via DROPOFF
      if (edge.end_id === toId && edge.label === 'DROPOFF') {
        results.push({
          path: newPath,
          edges: newEdges,
          depth: newDepth,
          totalPriority: newPriority,
        });
        minDepthFound = Math.min(minDepthFound, newDepth);
        continue;
      }

      // Continue exploring if this is PICKUP or TRANSFER (leading to/between hubs)
      if (edge.label === 'PICKUP' || edge.label === 'TRANSFER') {
        queue.push({
          path: newPath,
          edges: newEdges,
          depth: newDepth,
          totalPriority: newPriority,
        });
      }
    }
  }

  if (results.length === 0) {
    return null;
  }

  // Sort by: 1) depth (fewer legs = better), 2) priority (lower = better)
  results.sort((a, b) => {
    if (a.depth !== b.depth) return a.depth - b.depth;
    return a.totalPriority - b.totalPriority;
  });

  return results[0];
}

// Main routing function (V2 - connections based)
export function getRouteV2(
  request: RoutingRequest,
  networkName: string,
  serviceability: Serviceability[],
  hubs: Hub[],
  connections: Connection[]
): Route | null {
  const network = networkName.toLowerCase();

  // Build graph using V2 format
  const nodes = buildNodes(network, serviceability, hubs, connections);
  const edges = buildEdgesV2(network, serviceability, connections, hubs);

  // Create lookup maps
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  // Build source/destination IDs
  const fromId = buildLocalityId(
    network,
    request.from.postcode,
    request.from.suburb,
    request.from.state
  );
  const toId = buildLocalityId(network, request.to.postcode, request.to.suburb, request.to.state);

  // Check if source and destination exist
  if (!nodeMap.has(fromId)) {
    return null;
  }
  if (!nodeMap.has(toId)) {
    return null;
  }

  // Find best route (considers priority)
  const result = findBestRoute(fromId, toId, edges, network);

  if (!result || result.edges.length === 0) {
    return null;
  }

  // Convert path to route legs
  const legs: RouteLeg[] = [];

  for (const edge of result.edges) {
    const fromNode = nodeMap.get(edge.start_id);
    const toNode = nodeMap.get(edge.end_id);
    const courierZoneNode = nodeMap.get(edge.courier_zone_id);

    if (!fromNode || !toNode || !courierZoneNode) continue;

    legs.push({
      courier: {
        code: (courierZoneNode.data as { courier: string }).courier,
        sortCode: courierZoneNode.name,
        account: edge.account || undefined,
      },
      from: {
        type: fromNode.type,
        id: fromNode.id,
        name: fromNode.name,
        address:
          edge.start_id === fromId
            ? { countryCode: 'AU', ...request.from }
            : { streetAddress: '', ...(fromNode.data as Address) },
      },
      to: {
        type: toNode.type,
        id: toNode.id,
        name: toNode.name,
        address:
          edge.end_id === toId
            ? { countryCode: 'AU', ...request.to }
            : { streetAddress: '', ...(toNode.data as Address) },
      },
    });
  }

  // Build compatibility info
  const lastLeg = legs[legs.length - 1];
  const compatibility = {
    depotCode: lastLeg?.from.name ?? '?',
    lyneSortCode: lastLeg
      ? `${lastLeg.from.name}-${lastLeg.courier.code}-${lastLeg.courier.sortCode}`
      : '?-?-?',
    lyneZone: 'LYNE-ZONE',
  };

  return { legs, compatibility };
}

// Get unique localities from serviceability data for autocomplete
export function getUniqueLocalities(serviceability: Serviceability[]): Address[] {
  const seen = new Set<string>();
  const localities: Address[] = [];

  for (const row of serviceability) {
    const key = `${row.postcode}-${row.suburb}-${row.state}`.toUpperCase();
    if (!seen.has(key)) {
      seen.add(key);
      localities.push({
        suburb: row.suburb,
        postcode: row.postcode,
        state: row.state,
      });
    }
  }

  return localities.sort((a, b) => a.suburb.localeCompare(b.suburb));
}
